'use client';
import React, { useRef, useState, useEffect } from 'react';
import {
    GoogleMap,
    LoadScript,
    Autocomplete,
    Marker,
    DirectionsRenderer,
} from '@react-google-maps/api';
import henceforthApi from '../utils/henceforthApi';

const libraries = ['places'];

const GoogleMapComponent = ({
    defaultCenter,
    setStartAddress,
    setEndAddress,
    setStartPlace,
    setEndPlace,
    setDistanced,
    setSnapshotUrl,
    start,
    end,
    googleMapsApiKey
}) => {
    const autocompleteStartRef = useRef(null);
    const autocompleteEndRef = useRef(null);
    const [map, setMap] = useState(null);
    const [isMapsApiLoaded, setIsMapsApiLoaded] = useState(false);
    const [center, setCenter] = useState(defaultCenter);
    const [startValue, setStartValue] = useState('');
    const [endValue, setEndValue] = useState('');
    const [startLocation, setStartLocation] = useState(null);
    const [endLocation, setEndLocation] = useState(null);
    const [directions, setDirections] = useState(null);
    const [distance, setDistance] = useState(null);

    const handlePlaceSelected = (type) => {
        const ref = type === 'start' ? autocompleteStartRef.current : autocompleteEndRef.current;
        if (!ref) return;

        const place = ref.getPlace();
        if (place?.geometry) {
            const position = {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
            };

            if (type === 'start') {
                setStartValue(place.formatted_address);
                setStartPlace(place);
                setStartAddress({
                    place: place.formatted_address,
                    location: position
                });
                setStartLocation(position);
                setCenter(position);
            } else {
                setEndValue(place.formatted_address);
                setEndPlace(place);
                setEndAddress({
                    place: place.formatted_address,
                    location: position
                });
                setEndLocation(position);
                setCenter(position);
            }
        }
    };

    useEffect(() => {
        if (start && end) {
            setStartValue(start.place);
            setEndValue(end.place);
            setStartLocation({
                lat: start.location.lat,
                lng: start.location.lng
            });
            setEndLocation({
                lat: end.location.lat,
                lng: end.location.lng
            });
        }
    }, [start, end]);

    // Get directions polyline from backend API (avoids CORS issue)
    const getDirectionsPolylineWithDio = async (startLat, startLng, endLat, endLng) => {
        try {
            // Call backend API endpoint
            const url = `Admin/directions?startLat=${startLat}&startLng=${startLng}&endLat=${endLat}&endLng=${endLng}`;
            
            console.log('Fetching directions from backend:', url);
            
            // Use henceforthApi to call backend (same pattern as other API calls)
            const data = await henceforthApi.Common.get(url);
            
            console.log('Response data from backend:', data);
            
            if (data && data.status === 'OK') {
                if (data.routes && data.routes.length > 0) {
                    // Extract polyline exactly like Flutter
                    const polyline = data.routes[0].overview_polyline.points;
                    
                    if (!polyline) {
                        throw new Error('No polyline found in routes');
                    }
                    
                    console.log('Extracted polyline:', polyline);
                    return polyline;
                } else {
                    throw new Error('No routes found between the given locations');
                }
            } else {
                throw new Error(`Failed to fetch directions: ${data?.status || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error fetching directions from backend:', error);
            throw error;
        }
    };

    // MATCH FLUTTER EXACTLY - Generate static map URL with polyline
    const generateStaticMapUrlWithPolyline = (polyline, startLat, startLng, endLat, endLng) => {
        // Build URL string exactly like Flutter (string concatenation)
        const url = `https://maps.googleapis.com/maps/api/staticmap?size=512x512&path=enc:${polyline}&markers=color:green|size:mid|${startLat},${startLng}&markers=color:red|size:mid|${endLat},${endLng}&key=${process.env.REACT_APP_GOOGLE_MAP_KEY}`;
        
        console.log('Generated snapshot URL:', url);
        return url;
    };

    useEffect(() => {
        if (isMapsApiLoaded && startLocation && endLocation) {
            const directionsService = new window.google.maps.DirectionsService();
            
            // CRITICAL FIX: Use DRIVING mode to match Flutter (default is DRIVING anyway)
            directionsService.route(
                {
                    origin: startLocation,
                    destination: endLocation,
                    travelMode: window.google.maps.TravelMode.DRIVING  // Changed from WALKING to DRIVING
                },
                async (result, status) => {
                    if (status === window.google.maps.DirectionsStatus.OK && result) {
                        setDirections(result);
                        const routeLeg = result.routes[0].legs[0];
                        const dist = routeLeg?.distance?.value || null;
                        setDistance(dist);
                        setDistanced(dist);

                        // Get polyline using REST API (matching Flutter exactly)
                        try {
                            const polyline = await getDirectionsPolylineWithDio(
                                startLocation.lat,
                                startLocation.lng,
                                endLocation.lat,
                                endLocation.lng
                            );
                            
                            if (polyline && polyline.length > 0) {
                                // Generate static map URL exactly like Flutter
                                const url = generateStaticMapUrlWithPolyline(
                                    polyline,
                                    startLocation.lat,
                                    startLocation.lng,
                                    endLocation.lat,
                                    endLocation.lng
                                );
                                
                                console.log('Final snapshot URL:', url);
                                setSnapshotUrl(url);
                            } else {
                                console.error('Polyline is empty');
                            }
                        } catch (error) {
                            console.error('Failed to get polyline:', error);
                        }
                    } else {
                        console.error('Directions request failed:', status);
                    }
                }
            );
        }
    }, [isMapsApiLoaded, startLocation, endLocation]);

    const googleMapsApiKey22 = process.env.REACT_APP_GOOGLE_MAP_KEY;

    console.log("Google Maps API Key:", googleMapsApiKey22);
    
    return (
        <LoadScript
            googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_KEY}
            libraries={libraries}
            onLoad={() => setIsMapsApiLoaded(true)}
        >
            <div className="d-flex h-100 w-100 flex-column gap-2">
                <div className="flex flex-col gap-1">
                    <label className="font-semibold text-sm">Start Location</label>
                    <Autocomplete
                        onLoad={(ref) => (autocompleteStartRef.current = ref)}
                        onPlaceChanged={() => handlePlaceSelected('start')}
                    >
                        <input
                            type="text"
                            placeholder="Start point"
                            className="w-100 rounded border p-2"
                            value={startValue}
                            onChange={(e) => setStartValue(e.target.value)}
                        />
                    </Autocomplete>
                </div>

                <div className="flex flex-col gap-1">
                    <label className="font-semibold text-sm">End Location</label>
                    <Autocomplete
                        onLoad={(ref) => (autocompleteEndRef.current = ref)}
                        onPlaceChanged={() => handlePlaceSelected('end')}
                    >
                        <input
                            type="text"
                            placeholder="End point"
                            className="w-100 rounded border p-2"
                            value={endValue}
                            onChange={(e) => setEndValue(e.target.value)}
                        />
                    </Autocomplete>
                </div>

                <div className="map-wrapper w-100" style={{ height: '400px' }}>
                    <GoogleMap
                        center={center}
                        zoom={12}
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                        onLoad={(mapInstance) => setMap(mapInstance)}
                    >
                        {directions && <DirectionsRenderer directions={directions} />}
                    </GoogleMap>
                </div>

                {distance && (
                    <p className="mt-2 font-medium text-sm text-gray-700">
                        Distance: {(distance / 1000).toFixed(2)} km
                    </p>
                )}
            </div>
        </LoadScript>
    );
};

export default GoogleMapComponent;